import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Hand,
  Check,
  X,
  Plus,
  Minus,
  RotateCw,
  RotateCcw,
  ArrowRight,
  ArrowLeft,
  Maximize2,
  Minimize2,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import {
  applyLiquifyBrush,
  rasterizeLiquify,
  resetLiquifyField,
  type LiquifyTool,
} from '../lib/liquify-field';

/** Liquify full-screen workspace — Tier 1 Group E (final piece).
 *
 *  Brush-based mesh deformation. Each brush variant applies a
 *  different displacement-field operator under the cursor:
 *
 *    - Push      — translate sampled pixels in the stroke direction
 *    - Pull      — translate sampled pixels TOWARD the cursor
 *    - Twirl CW  — rotate sampled pixels clockwise around the cursor
 *    - Twirl CCW — rotate sampled pixels counter-clockwise
 *    - Pucker    — squeeze pixels toward the cursor (radial shrink)
 *    - Bloat     — expand pixels away from the cursor (radial grow)
 *    - Reconstruct — locally undo the displacement (drag-to-restore)
 *
 *  Plus a Hand tool for pan (mirrors the Refine Edge convention).
 *
 *  Architecture: a downscaled displacement field (Float32Array of
 *  (dx, dy) pairs at every cell) is the working state — see
 *  `../lib/liquify-field.ts` for the math. Brush strokes read-
 *  modify-write a region of the field; the preview canvas re-
 *  rasterizes by sampling source pixels at (outputX + dx,
 *  outputY + dy) — pure inverse mapping. Apply re-runs the same
 *  rasterizer at full source resolution with bilinear sampling.
 */

/** Workspace-level tool union — extends the lib's brush-operator
 *  set with `'hand'` (a non-mutating pan tool that has no
 *  corresponding displacement operator). The lib's `LiquifyTool`
 *  is the canonical brush set; the workspace narrows to it at
 *  call sites before invoking the operator. */
type WorkspaceTool = LiquifyTool | 'hand';

interface LiquifyWorkspaceProps {
  /** Source canvas to deform. Captured at modal-open time so the
   *  preview is stable while the user works. Same `HTMLCanvasElement`-
   *  rather-than-`ImageData` contract as RefineEdgeWorkspace — see
   *  ColorRangeDialog comment for the 4K perf rationale. */
  source: HTMLCanvasElement;
  onClose: () => void;
  /** Apply — caller bakes the returned canvas back into the active
   *  layer (mirrors Free Transform Phase 1b's
   *  `commitNonAffineDeformation`: `target.setElement(canvas)` +
   *  matrix reset). Always passed at full source dimensions. */
  onApply: (deformedCanvas: HTMLCanvasElement) => void;
}

const PREVIEW_MAX = 720;
const DISPLACEMENT_FIELD_MAX = 512;
/** Peak per-dab displacement (in source pixels) at Pressure=100,
 *  expressed as a fraction of the brush diameter. Tuned so a
 *  default-pressure (50) stroke moves pixels by an amount that's
 *  visible on each dab without immediately shredding the mesh.
 *  Lowering this value makes Liquify feel "lazier"; raising it
 *  makes it more aggressive. */
const PEAK_STRENGTH_PER_BRUSH_DIAMETER = 1 / 12;

const TOOLS: { value: WorkspaceTool; label: string; Icon: LucideIcon }[] = [
  { value: 'push', label: 'Forward Warp (Push)', Icon: ArrowRight },
  { value: 'pull', label: 'Pull', Icon: ArrowLeft },
  { value: 'twirl-cw', label: 'Twirl Clockwise', Icon: RotateCw },
  { value: 'twirl-ccw', label: 'Twirl Counter-Clockwise', Icon: RotateCcw },
  { value: 'pucker', label: 'Pucker', Icon: Minimize2 },
  { value: 'bloat', label: 'Bloat', Icon: Maximize2 },
  { value: 'reconstruct', label: 'Reconstruct', Icon: Undo2 },
  { value: 'hand', label: 'Hand', Icon: Hand },
];

export function LiquifyWorkspace({ source, onClose, onApply }: LiquifyWorkspaceProps) {
  // Brush parameters. Defaults chosen to feel responsive without
  // catastrophically deforming the layer on the first dab — a user
  // who's never used Liquify before should get a recognizable
  // result, not pixel soup.
  const [tool, setTool] = useState<WorkspaceTool>('push');
  const [brushSize, setBrushSize] = useState(120);
  const [brushPressure, setBrushPressure] = useState(50);
  const [brushDensity, setBrushDensity] = useState(50);

  // Working state. The displacement field is a Float32Array of
  // (dx, dy) pairs at every cell of a downscaled grid — full-source-
  // resolution displacement would burn megabytes for a 4K image and
  // make per-dab read/writes painful. Brush dabs mutate this field;
  // the preview rasterizer reads it to inverse-map source pixels.
  const displacementFieldRef = useRef<Float32Array | null>(null);
  const displacementDimsRef = useRef<{ width: number; height: number } | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const strokeRef = useRef<{ lastX: number; lastY: number } | null>(null);

  // Cursor tracking — drives the SVG brush-size circle. Same pattern
  // as Refine Edge / main editor.
  const [brushCursor, setBrushCursor] = useState<{ x: number; y: number; visible: boolean }>({
    x: 0,
    y: 0,
    visible: false,
  });

  // Zoom / pan. Liquify especially needs fine zoom — selective face
  // retouching often happens at 200%+ where individual eyelash-
  // width displacements are meaningful.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [spaceHeld, setSpaceHeld] = useState(false);
  const panDragRef = useRef<{
    startX: number;
    startY: number;
    startPanX: number;
    startPanY: number;
  } | null>(null);

  // Recompute ticker — bumped on each brush dab so the rasterize
  // effect (whose deps include `recomputeTick`) re-fires and
  // refreshes the preview from the freshly-mutated field. Same
  // pattern as Refine Edge.
  const [recomputeTick, setRecomputeTick] = useState(0);
  const triggerRecompute = () => setRecomputeTick((t) => t + 1);

  const previewDims = useMemo(() => {
    const longest = Math.max(source.width, source.height);
    const scale = longest > PREVIEW_MAX ? PREVIEW_MAX / longest : 1;
    return {
      width: Math.round(source.width * scale),
      height: Math.round(source.height * scale),
      scale,
    };
  }, [source]);

  // Allocate the displacement field on open. Sized so the longest
  // axis is at most DISPLACEMENT_FIELD_MAX cells, which keeps
  // memory + per-dab cost bounded regardless of source dimensions.
  useEffect(() => {
    const longest = Math.max(source.width, source.height);
    const fieldScale = longest > DISPLACEMENT_FIELD_MAX ? DISPLACEMENT_FIELD_MAX / longest : 1;
    const w = Math.max(1, Math.round(source.width * fieldScale));
    const h = Math.max(1, Math.round(source.height * fieldScale));
    displacementFieldRef.current = new Float32Array(w * h * 2);
    displacementDimsRef.current = { width: w, height: h };
  }, [source]);

  /** Apply a brush dab at source-pixel coords (x, y). Mutates the
   *  displacement field in-place over a brush radius of
   *  `brushSize/2` source pixels around (x, y). Hand tool is
   *  excluded at the call site so we know `tool` is a real brush
   *  here.
   *
   *  Unit conversion: source-pixel coords → field-cell coords via
   *  the same scale used to allocate the field. Brush radius
   *  converts the same way. The displacement values stored ARE in
   *  source-pixel units, so the rasterizer can add them directly
   *  to source positions. */
  const applyBrushDab = useCallback(
    (x: number, y: number, strokeDx: number, strokeDy: number) => {
      if (tool === 'hand') return;
      const field = displacementFieldRef.current;
      const dims = displacementDimsRef.current;
      if (!field || !dims) return;
      const scaleX = dims.width / source.width;
      const scaleY = dims.height / source.height;
      // Brush radius in field cells uses the geometric mean of the
      // axes (sources with extreme aspect ratios still get a roughly
      // circular brush in field space).
      const radiusCells = (brushSize / 2) * Math.sqrt(scaleX * scaleY);
      // Strength in source pixels: Pressure scales the peak per-
      // dab displacement, capped at PEAK_STRENGTH_PER_BRUSH_DIAMETER
      // of the brush diameter.
      const strength = (brushPressure / 100) * (brushSize * PEAK_STRENGTH_PER_BRUSH_DIAMETER);
      const hardness = brushDensity / 100;
      applyLiquifyBrush(field, dims.width, dims.height, {
        tool,
        cx: x * scaleX,
        cy: y * scaleY,
        radius: radiusCells,
        strength,
        hardness,
        strokeDx,
        strokeDy,
      });
    },
    [tool, brushSize, brushPressure, brushDensity, source.width, source.height],
  );

  /** Rasterize the deformed preview by inverse-mapping every
   *  output pixel through the displacement field. The preview
   *  uses nearest-neighbor source sampling for interactivity; the
   *  Apply-time bake (in `handleApply`) uses bilinear sampling for
   *  quality. */
  const rasterizePreview = useCallback(() => {
    const preview = previewCanvasRef.current;
    const field = displacementFieldRef.current;
    const dims = displacementDimsRef.current;
    if (!preview || !field || !dims) return;
    rasterizeLiquify(source, field, dims.width, dims.height, preview, {
      sourceFilter: 'nearest',
    });
  }, [source]);

  // First-paint + parameter-change recompute. rAF deferral matches
  // Refine Edge: the preview canvas mounts inside a Radix Portal and
  // its drawing surface isn't fully wired up on the first sync
  // effect run.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      rasterizePreview();
    });
    return () => cancelAnimationFrame(id);
  }, [rasterizePreview, recomputeTick]);

  // ─── Brush stroke handlers ───────────────────────────────────────
  const previewToSource = (e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * source.width;
    const py = ((e.clientY - rect.top) / rect.height) * source.height;
    return { x: px, y: py };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool === 'hand' || spaceHeld) return;
    const p = previewToSource(e);
    strokeRef.current = { lastX: p.x, lastY: p.y };
    applyBrushDab(p.x, p.y, 0, 0);
    triggerRecompute();
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool === 'hand' || spaceHeld || !strokeRef.current) return;
    const p = previewToSource(e);
    const dx = p.x - strokeRef.current.lastX;
    const dy = p.y - strokeRef.current.lastY;
    const dist = Math.hypot(dx, dy);
    const spacing = Math.max(1, brushSize / 4);
    if (dist < spacing) return;
    const steps = Math.ceil(dist / spacing);
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const sx = strokeRef.current.lastX + dx * t;
      const sy = strokeRef.current.lastY + dy * t;
      applyBrushDab(sx, sy, dx / steps, dy / steps);
    }
    strokeRef.current.lastX = p.x;
    strokeRef.current.lastY = p.y;
    triggerRecompute();
  };

  const handleMouseUp = () => {
    strokeRef.current = null;
  };

  // ─── Zoom / pan ──────────────────────────────────────────────────
  const applyZoomAtCursor = useCallback(
    (newZoom: number, clientX: number, clientY: number, rect: DOMRect) => {
      const clamped = Math.max(0.25, Math.min(8, newZoom));
      if (clamped === zoom) return;
      const cxRel = clientX - rect.left - rect.width / 2;
      const cyRel = clientY - rect.top - rect.height / 2;
      const ratio = clamped / zoom;
      setPan({
        x: cxRel - ratio * (cxRel - pan.x),
        y: cyRel - ratio * (cyRel - pan.y),
      });
      setZoom(clamped);
    },
    [zoom, pan],
  );

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const factor = Math.exp(-e.deltaY * 0.002);
    applyZoomAtCursor(zoom * factor, e.clientX, e.clientY, e.currentTarget.getBoundingClientRect());
  };

  const panModeActive = spaceHeld || tool === 'hand';

  const handlePanStart = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!panModeActive) return;
    e.preventDefault();
    panDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
  };

  const handlePanMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const drag = panDragRef.current;
    if (!drag) return;
    setPan({
      x: drag.startPanX + (e.clientX - drag.startX),
      y: drag.startPanY + (e.clientY - drag.startY),
    });
  };

  const handlePanEnd = () => {
    panDragRef.current = null;
  };

  useEffect(() => {
    const isFormElement = (el: EventTarget | null) =>
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !isFormElement(e.target)) {
        setSpaceHeld(true);
        e.preventDefault();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setSpaceHeld(false);
        panDragRef.current = null;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // ─── Apply / Cancel ──────────────────────────────────────────────
  const handleApply = () => {
    const field = displacementFieldRef.current;
    const dims = displacementDimsRef.current;
    const out = document.createElement('canvas');
    out.width = source.width;
    out.height = source.height;
    if (field && dims) {
      // Bilinear source sampling for the final bake — quality
      // matters more than interactivity here. Cost is bounded:
      // O(source.width × source.height × constant), runs once
      // when the user clicks Done.
      rasterizeLiquify(source, field, dims.width, dims.height, out, {
        sourceFilter: 'bilinear',
      });
    } else {
      // Field allocation failed — degrade to a straight blit so the
      // round-trip still completes and the user doesn't lose work.
      out.getContext('2d')?.drawImage(source, 0, 0);
    }
    onApply(out);
  };

  const handleResetField = () => {
    const field = displacementFieldRef.current;
    if (!field) return;
    resetLiquifyField(field);
    triggerRecompute();
  };

  return (
    <DialogPrimitive.Root open onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90" />
        <DialogPrimitive.Content className="fixed inset-0 z-50 flex flex-col bg-background">
          {/* Top toolbar */}
          <div className="flex items-center justify-between border-b border-border px-4 py-2 bg-background">
            <div className="flex items-center gap-2">
              <DialogPrimitive.Title className="text-sm font-semibold">
                Liquify
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                Full-screen workspace for brush-based mesh deformation. Pick a tool from the
                mini-palette, set brush size / pressure / density, then drag on the canvas to deform
                the active layer. Apply to bake.
              </DialogPrimitive.Description>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleResetField}
                className="px-3 py-1.5 rounded border border-border text-xs hover:bg-accent"
                title="Reset all deformation"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 rounded border border-border text-xs hover:bg-accent"
                title="Cancel"
              >
                <X className="h-4 w-4 inline-block" />
                <span className="sr-only">Cancel</span>
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-xs hover:opacity-90"
                title="Done — apply deformation"
              >
                <Check className="h-4 w-4 inline-block mr-1" />
                Done
              </button>
            </div>
          </div>

          <div className="flex-1 flex overflow-hidden">
            {/* Mini palette */}
            <div className="w-12 border-r border-border bg-background flex flex-col items-center py-2 gap-1">
              {TOOLS.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTool(value)}
                  className={`w-9 h-9 flex items-center justify-center rounded ${
                    tool === value ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                  }`}
                  title={label}
                  aria-label={label}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>

            {/* Preview viewport */}
            <div
              className="flex-1 relative overflow-hidden bg-[#1a1a1a] select-none"
              onWheel={handleWheel}
              onMouseDown={handlePanStart}
              onMouseMove={(e) => {
                handlePanMove(e);
                const rect = e.currentTarget.getBoundingClientRect();
                setBrushCursor({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top,
                  visible: true,
                });
              }}
              onMouseUp={handlePanEnd}
              onMouseLeave={() => {
                handlePanEnd();
                setBrushCursor((c) => ({ ...c, visible: false }));
              }}
              onMouseEnter={() => setBrushCursor((c) => ({ ...c, visible: true }))}
              style={{
                cursor: panModeActive ? (panDragRef.current ? 'grabbing' : 'grab') : 'none',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: 'center',
                  imageRendering: 'pixelated',
                }}
              >
                <canvas
                  ref={previewCanvasRef}
                  width={previewDims.width}
                  height={previewDims.height}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  className={
                    panModeActive
                      ? panDragRef.current
                        ? 'cursor-grabbing'
                        : 'cursor-grab'
                      : 'cursor-crosshair'
                  }
                  style={{
                    imageRendering: 'pixelated',
                    display: 'block',
                  }}
                />
              </div>

              {brushCursor.visible && !panModeActive && (
                <svg
                  className="absolute pointer-events-none"
                  style={{
                    left: brushCursor.x - (brushSize / 2) * previewDims.scale * zoom,
                    top: brushCursor.y - (brushSize / 2) * previewDims.scale * zoom,
                    width: brushSize * previewDims.scale * zoom,
                    height: brushSize * previewDims.scale * zoom,
                  }}
                  viewBox="0 0 100 100"
                >
                  <circle cx="50" cy="50" r="48" fill="none" stroke="black" strokeWidth="3" />
                  <circle cx="50" cy="50" r="48" fill="none" stroke="white" strokeWidth="1.5" />
                </svg>
              )}

              {/* Zoom controls */}
              <div className="absolute bottom-3 right-3 flex items-center gap-0.5 bg-background/90 border border-border rounded p-0.5 text-xs">
                <button
                  type="button"
                  onClick={() =>
                    applyZoomAtCursor(
                      zoom / 1.25,
                      window.innerWidth / 2,
                      window.innerHeight / 2,
                      new DOMRect(0, 0, window.innerWidth, window.innerHeight),
                    )
                  }
                  className="w-7 h-7 flex items-center justify-center rounded hover:bg-accent"
                  title="Zoom out"
                  aria-label="Zoom out"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={resetZoom}
                  className="px-2 h-7 flex items-center justify-center rounded hover:bg-accent tabular-nums min-w-[3.5rem]"
                  title="Reset zoom"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  type="button"
                  onClick={() =>
                    applyZoomAtCursor(
                      zoom * 1.25,
                      window.innerWidth / 2,
                      window.innerHeight / 2,
                      new DOMRect(0, 0, window.innerWidth, window.innerHeight),
                    )
                  }
                  className="w-7 h-7 flex items-center justify-center rounded hover:bg-accent"
                  title="Zoom in"
                  aria-label="Zoom in"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="absolute bottom-3 left-3 text-[10px] text-muted-foreground bg-background/80 border border-border rounded px-2 py-1 pointer-events-none">
                Wheel to zoom · hold Space + drag to pan
              </div>
            </div>

            {/* Right panel */}
            <div className="w-72 border-l border-border bg-background overflow-y-auto">
              <div className="p-4 space-y-5 text-xs">
                <section className="space-y-2">
                  <h4 className="font-medium text-muted-foreground uppercase">Brush</h4>
                  <label className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16">Size</span>
                    <input
                      type="range"
                      min={10}
                      max={600}
                      value={brushSize}
                      onChange={(e) => setBrushSize(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-10 text-right tabular-nums">{brushSize}</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16">Pressure</span>
                    <input
                      type="range"
                      min={1}
                      max={100}
                      value={brushPressure}
                      onChange={(e) => setBrushPressure(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-10 text-right tabular-nums">{brushPressure}</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16">Density</span>
                    <input
                      type="range"
                      min={1}
                      max={100}
                      value={brushDensity}
                      onChange={(e) => setBrushDensity(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="w-10 text-right tabular-nums">{brushDensity}</span>
                  </label>
                  {/* Density controls the brush's falloff steepness:
                      low density = soft feathered edges, high density
                      = hard-edged dab. Pressure is overall deformation
                      magnitude per dab. */}
                </section>

                <section className="space-y-2">
                  <h4 className="font-medium text-muted-foreground uppercase">Active Tool</h4>
                  <p className="text-muted-foreground leading-relaxed">
                    {TOOLS.find((t) => t.value === tool)?.label ?? tool}
                  </p>
                </section>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
